import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  RadialLinearScale,
} from 'chart.js';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  RadialLinearScale
);

interface DataVisualizationProps {
  type: 'spending' | 'savings' | 'budget' | 'goals';
  /**
   * Expected data shapes:
   *
   * For type 'spending':
   *   - data.labels: string[]
   *   - data.spending: number[]
   *   - data.budget: number[]
   *
   * For type 'budget':
   *   - data.categories: string[]
   *   - data.amounts: number[]
   *
   * For type 'savings':
   *   - data.goals: string[]
   *   - data.current: number[]
   *
   * For type 'goals':
   *   - data.metrics: string[]
   *   - data.scores: number[]
   */
  data: any;
  className?: string;
}

const DataVisualization: React.FC<DataVisualizationProps> = ({
  type,
  data,
  className = '',
}) => {
  // Common chart options shared across all chart types.
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: 'rgba(255, 255, 255, 1)',
          font: {
            size: 13,
            weight: '500',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1e3a8a',
        bodyColor: '#1e3a8a',
        titleFont: {
          size: 14,
          weight: '600',
        },
        bodyFont: {
          size: 13,
        },
        padding: 12,
        cornerRadius: 8,
        boxPadding: 6,
      },
      title: {
        display: true,
        color: 'rgba(255, 255, 255, 0.9)',
        font: {
          size: 16,
          weight: '500',
        },
      },
    },
    layout: {
      padding: {
        top: 20,
        right: 20,
        bottom: 20,
        left: 20,
      },
    },
  };

  // Render a Spending Trends Line Chart.
  const renderSpendingTrends = () => {
    const options = {
      ...commonOptions,
      plugins: {
        ...commonOptions.plugins,
        title: {
          ...commonOptions.plugins.title,
          text: 'Monthly Spending Analysis',
        },
      },
      scales: {
        x: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.9)',
            font: {
              size: 12,
            },
          },
        },
        y: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.9)',
            font: {
              size: 12,
            },
            callback: (value: number) => `$${value.toLocaleString()}`,
          },
        },
      },
    };

    return (
      <Line
        data={{
          labels: data.labels,
          datasets: [
            {
              label: 'Regular',
              data: data.spending,
              borderColor: 'rgba(59, 130, 246, 1)',
              backgroundColor: 'rgba(59, 130, 246, 0.15)',
              borderWidth: 2,
              fill: true,
              tension: 0.4,
            },
            {
              label: 'Finance',
              data: data.budget,
              borderColor: 'rgba(139, 92, 246, 1)',
              backgroundColor: 'rgba(139, 92, 246, 0.15)',
              borderWidth: 2,
              fill: true,
              tension: 0.4,
            },
          ],
        }}
        options={options}
      />
    );
  };

  // Render a Budget Breakdown Doughnut Chart.
  const renderBudgetBreakdown = () => {
    const options = {
      ...commonOptions,
      cutout: '70%', // Defines the doughnut cutout size.
    };

    return (
      <Doughnut
        data={{
          labels: data.categories,
          datasets: [
            {
              data: data.amounts,
              backgroundColor: [
                'rgba(59, 130, 246, 0.8)',
                'rgba(139, 92, 246, 0.8)',
                'rgba(236, 72, 153, 0.8)',
                'rgba(34, 197, 94, 0.8)',
                'rgba(249, 115, 22, 0.8)',
              ],
              borderWidth: 2,
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
          ],
        }}
        options={options}
      />
    );
  };

  // Render a Savings Progress Bar Chart.
  const renderSavingsProgress = () => {
    const options = {
      ...commonOptions,
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        },
        y: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)',
            callback: (value: number) => `$${value.toLocaleString()}`,
          },
        },
      },
    };

    return (
      <Bar
        data={{
          labels: data.goals,
          datasets: [
            {
              label: 'Regular',
              data: data.current,
              backgroundColor: 'rgba(59, 130, 246, 0.8)',
            },
          ],
        }}
        options={options}
      />
    );
  };

  // Render a Financial Health Radar Chart.
  const renderFinancialHealth = () => {
    const options = {
      ...commonOptions,
      scales: {
        r: {
          angleLines: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
          pointLabels: {
            color: 'rgba(255, 255, 255, 0.7)',
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)',
            backdropColor: 'transparent',
          },
        },
      },
    };

    return (
      <Radar
        data={{
          labels: data.metrics,
          datasets: [
            {
              label: 'Current Score',
              data: data.scores,
              backgroundColor: 'rgba(59, 130, 246, 0.2)',
              borderColor: 'rgba(59, 130, 246, 0.8)',
              pointBackgroundColor: 'rgba(59, 130, 246, 0.8)',
              pointBorderColor: '#fff',
            },
          ],
        }}
        options={options}
      />
    );
  };

  // Helper to select the appropriate chart based on the type.
  const renderChart = () => {
    switch (type) {
      case 'spending':
        return renderSpendingTrends();
      case 'budget':
        return renderBudgetBreakdown();
      case 'savings':
        return renderSavingsProgress();
      case 'goals':
        return renderFinancialHealth();
      default:
        return null;
    }
  };

  // Determine header title and description based on the chart type.
  const getTitle = () => {
    switch (type) {
      case 'spending':
        return 'Spending Analysis';
      case 'budget':
        return 'Budget Breakdown';
      case 'savings':
        return 'Savings Progress';
      case 'goals':
        return 'Financial Health';
      default:
        return '';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'spending':
        return 'Track your monthly spending patterns and budget adherence';
      case 'budget':
        return 'View your budget allocation across different categories';
      case 'savings':
        return 'Monitor your progress towards savings goals';
      case 'goals':
        return 'Assess your overall financial health metrics';
      default:
        return '';
    }
  };

  return (
    <div className={`bg-blue-800/20 backdrop-blur-sm rounded-xl p-6 ${className}`}>
      <div className="mb-4">
        <h3 className="text-white/90 text-lg font-medium">{getTitle()}</h3>
      </div>
      <div className="h-[300px]">{renderChart()}</div>
      <div className="mt-4 text-white/70 text-sm">{getDescription()}</div>
    </div>
  );
};

export default DataVisualization;
